/**
 * CRM Context and Provider
 *
 * Manages CRM (Salesforce) customer data caching to minimize API calls.
 * Phone number is stored in CRM only (not SFCC), so this context caches
 * CRM data after login for use across the application.
 *
 * Features:
 * - Caches CRM profile data (including phone) after login
 * - Persists data across page navigations within session
 * - Provides methods to update cached data after profile changes
 * - Avoids repeated CRM API calls
 */

import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {useCustomerId} from '@salesforce/commerce-sdk-react';

import {clearCrmPreferencesCookie} from '@amblique/king-living/app/utils/crm-preferences-cookie';

/**
 * CRM Context
 */
const CrmContext = createContext(null);

/**
 * Clears cached CRM when the shopper logs out or a different registered customer
 * logs in, so phone/preferences from the previous session cannot leak into UI.
 *
 * @returns {null}
 */
function CrmCustomerIdSync() {
  const customerId = useCustomerId();
  const {clearCrmData} = useContext(CrmContext);
  const prevCustomerIdRef = useRef();

  useEffect(() => {
    const prev = prevCustomerIdRef.current;

    if (prev === customerId) {
      return;
    }

    prevCustomerIdRef.current = customerId;

    if (prev && !customerId) {
      clearCrmData();
      clearCrmPreferencesCookie();

      return;
    }

    if (prev && customerId && prev !== customerId) {
      clearCrmData();
      clearCrmPreferencesCookie();
    }
  }, [customerId, clearCrmData]);

  return null;
}

/**
 * CRM Provider
 *
 * Wraps the application to provide CRM data caching.
 * Should be placed inside the commerce-sdk-react providers.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement}
 */
export function CrmProvider({children}) {
  // CRM profile data cache
  const [crmProfile, setCrmProfile] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Site ID for which registration just synced (avoids duplicate sync from useCrmSiteSync)
  const registrationSyncedSiteIdRef = useRef(null);

  /**
   * Set CRM profile data (called after login or sync)
   * Extracts relevant fields from CRM response
   */
  const setCrmData = useCallback((crmData) => {
    if (!crmData) {
      setCrmProfile(null);
      setIsLoaded(true);

      return;
    }

    // Extract relevant fields from CRM Person Account data
    const profile = {
      id: crmData.Id,
      firstName: crmData.FirstName,
      lastName: crmData.LastName,
      email: crmData.PersonEmail,
      phoneMobile: crmData.PersonMobilePhone,
      countryCode: crmData.countryCode__c,
      preferredStoreId: crmData.Preferred_Store_Id__c,
      accountType: crmData.Type,
      externalId: crmData.externalReferenceIDSFCC__c,
      // Marketing preferences
      emailOptOut: crmData.PersonHasOptedOutOfEmail,
      smsOptIn: crmData.smsSubscribed__pc,
    };

    console.log('[CRM Context] Caching CRM profile:', {
      id: profile.id,
      hasPhone: !!profile.phoneMobile,
    });

    setCrmProfile(profile);
    setIsLoaded(true);
  }, []);

  /**
   * Update specific fields in the cached profile
   * Used after profile updates to keep cache in sync
   */
  const updateCrmProfile = useCallback((updates) => {
    setCrmProfile((prev) => {
      const updated = {...(prev || {}), ...updates};

      console.log('[CRM Context] Updated cached profile:', Object.keys(updates));

      return updated;
    });
    setIsLoaded(true);
  }, []);

  /**
   * Clear CRM data (called on logout)
   */
  const clearCrmData = useCallback(() => {
    console.log('[CRM Context] Clearing CRM data');
    setCrmProfile(null);
    setIsLoaded(false);
  }, []);

  /**
   * Mark that registration sync just completed for this site.
   * useCrmSiteSync skips its sync when this matches current site to avoid duplicate CRM accounts.
   */
  const markRegistrationSyncedForSite = useCallback((siteId) => {
    if (siteId) {
      registrationSyncedSiteIdRef.current = siteId;
    }
  }, []);

  /**
   * Get the site ID that registration synced for (if any). Read-only.
   */
  const getRegistrationSyncedSite = useCallback(() => registrationSyncedSiteIdRef.current, []);

  /**
   * Clear the registration-synced site flag. Call after consuming it in useCrmSiteSync.
   */
  const clearRegistrationSyncedSite = useCallback(() => {
    registrationSyncedSiteIdRef.current = null;
  }, []);

  const value = useMemo(() => ({
    // CRM profile data
    crmProfile,
    isLoaded,

    // Phone number helper (main use case)
    phoneMobile: crmProfile?.phoneMobile || '',

    // Marketing preferences helpers
    // Note: emailOptOut is inverted - true means opted OUT
    emailOptIn: crmProfile?.emailOptOut === false,
    smsOptIn: crmProfile?.smsOptIn === true,

    // Methods
    setCrmData,
    updateCrmProfile,
    clearCrmData,
    markRegistrationSyncedForSite,
    getRegistrationSyncedSite,
    clearRegistrationSyncedSite,
  }), [crmProfile, isLoaded, setCrmData, updateCrmProfile, clearCrmData, markRegistrationSyncedForSite, getRegistrationSyncedSite, clearRegistrationSyncedSite]);

  return (
    <CrmContext.Provider value={value}>
      <CrmCustomerIdSync />
      {children}
    </CrmContext.Provider>
  );
}

CrmProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access CRM context
 *
 * @returns {Object} CRM context value
 * @throws {Error} If used outside CrmProvider
 *
 * @example
 * // Get phone number from CRM
 * const {phoneMobile} = useCrm();
 *
 * @example
 * // Cache CRM data after login
 * const {setCrmData} = useCrm();
 * const result = await syncLogin({customerNo});
 * if (result.crm) setCrmData(result.crm);
 *
 * @example
 * // Update cached data after profile edit
 * const {updateCrmProfile} = useCrm();
 * updateCrmProfile({phoneMobile: newPhone});
 */
export function useCrm() {
  const context = useContext(CrmContext);

  if (context === null) {
    throw new Error('useCrm must be used within CrmProvider');
  }

  return context;
}
